import { useState, useEffect, useRef, useCallback } from 'react'
import { SSEData } from './types'
import { readStream } from './streamUtils'
import {
  DEFAULT_RETRY_ATTEMPTS,
  DEFAULT_RETRY_DELAY,
  getDefaultSSEHeaders,
} from '../../Config/SSE/sse.config'
import { SSEConfig } from '../../Api/sse/sse.types'
import { validateSSEBrowserSupport } from '../../Utils/Window/windowHelpers'
import { fetchSSEStream } from '../../Api/sse/sse.api'

interface UseSSEReturn<T> {
  data: T | null
  error: Error | null
  isConnected: boolean
  connect: () => Promise<void>
  disconnect: () => void
}

const useServerSent = <T,>({
  url,
  headers = getDefaultSSEHeaders(),
  options = {},
}: SSEConfig): UseSSEReturn<T> => {
  const { retryAttempts = DEFAULT_RETRY_ATTEMPTS, retryDelay = DEFAULT_RETRY_DELAY } =
    options

  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const [manualDisconnect, setManualDisconnect] = useState(false)
  const [noLiveGame, setNoLiveGame] = useState(false)
  const lastDataRef = useRef<T | null>(null)

  const refs = {
    controller: useRef<AbortController | null>(null),
    reader: useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null),
    isMounted: useRef(true),
    reconnectTimeout: useRef<NodeJS.Timeout | null>(null),
    connect: useRef<() => Promise<void>>(null),
  }

  const isGameEndedOrNoGame = useCallback((currentData: SSEData | null): boolean => {
    if (!currentData) return false
    console.log('isGameEndedOrNoGame currentData:', currentData)
    return (
      currentData.type === 'connection' &&
      (currentData.data === 'No live game' || currentData.data === 'Game ended')
    )
  }, [])

  const disconnect = useCallback(
    (isManual: boolean = true): void => {
      if (isManual) {
        setManualDisconnect(true)
      }

      if (refs.controller.current) {
        try {
          refs.controller.current.abort()
        } catch (err) {
          if (err instanceof Error && err.name !== 'AbortError') {
            console.error('Error aborting controller:', err)
          }
        }
        refs.controller.current = null
      }

      if (refs.reader.current) {
        refs.reader.current.cancel().catch((err) => {
          if (err instanceof Error && err.name !== 'AbortError') {
            console.error('Error cancelling reader:', err)
          }
        })
        refs.reader.current = null
      }

      if (refs.reconnectTimeout.current) {
        clearTimeout(refs.reconnectTimeout.current)
        refs.reconnectTimeout.current = null
      }

      if (refs.isMounted.current) {
        setIsConnected(false)
        if (!isGameEndedOrNoGame(lastDataRef.current as SSEData)) {
          setData(null)
        }
        setError(null)
      }
      setRetryCount(0)
    },
    [isGameEndedOrNoGame]
  )

  const attemptReconnect = useCallback((): void => {
    if (
      manualDisconnect ||
      noLiveGame ||
      isGameEndedOrNoGame(lastDataRef.current as SSEData)
    ) {
      console.log(
        noLiveGame || isGameEndedOrNoGame(lastDataRef.current as SSEData)
          ? 'Game not available or ended, skipping reconnection'
          : 'Manual disconnect detected, skipping reconnection'
      )
      return
    }

    if (retryCount < retryAttempts) {
      const delay = retryDelay * Math.pow(2, retryCount)
      console.log(`Attempting to reconnect in ${delay}ms...`)
      refs.reconnectTimeout.current = setTimeout(() => {
        setRetryCount((prev) => prev + 1)
        refs.connect.current?.()
      }, delay)
    } else {
      console.error('Max reconnection attempts reached.')
      if (refs.isMounted.current) {
        setError(new Error('Failed to reconnect to SSE after multiple attempts.'))
      }
    }
  }, [
    retryAttempts,
    retryDelay,
    retryCount,
    manualDisconnect,
    noLiveGame,
    isGameEndedOrNoGame,
  ])

  const connect = useCallback(async (): Promise<void> => {
    if (isConnected) {
      console.warn('SSE connection already exists.')
      return
    }

    setManualDisconnect(false)
    setNoLiveGame(false)

    if (!validateSSEBrowserSupport()) {
      setError(new Error('Browser does not support required APIs for SSE.'))
      return
    }

    const controller = new AbortController()
    refs.controller.current = controller

    try {
      const { reader } = await fetchSSEStream(url, headers, controller.signal)

      console.log('SSE Reader created successfully')
      refs.reader.current = reader
      setIsConnected(true)
      setRetryCount(0)
      setError(null)

      await readStream(reader, {
        onData: (parsedData: SSEData) => {
          let cleanedSSEData = parsedData
          const sseEvent = cleanedSSEData?.type
          let gameData = cleanedSSEData?.data
          if (refs.isMounted.current) {
            if (sseEvent === 'eventUpdate' && typeof gameData !== 'string') {
              if (gameData?.playerStats) {
                gameData.playerStats = gameData.playerStats.map((stat: any) => {
                  const rawId = String(stat.officialId || '')
                  const paddedOfficialId =
                    rawId.length >= 6 ? rawId : rawId.padStart(6, '0')
                  return {
                    ...stat,
                    officialId: paddedOfficialId,
                  }
                })
              }
              cleanedSSEData.data = gameData
              console.log('useServerSent onData cleanedSSEData:', cleanedSSEData)
              setData(cleanedSSEData as T)
            } else {
              setData(cleanedSSEData as T)
            }
            lastDataRef.current = cleanedSSEData as T
            console.log('Result of isGameEndedOrNoGame:', isGameEndedOrNoGame(cleanedSSEData))
            console.log('useServerSent onData cleanedSSEData:', cleanedSSEData)

            if (isGameEndedOrNoGame(cleanedSSEData)) {
              console.log('Game not available or ended - disconnecting')
              setNoLiveGame(true)
              disconnect(false)
            }
          }
        },
        onError: (readError: Error) => {
          if (refs.isMounted.current) {
            if (readError.name === 'AbortError') {
              console.log('SSE stream aborted (manual disconnect)')
              return
            }

            console.error('SSE Read Error:', readError)
            setError(readError)
            setIsConnected(false)
            if (readError.message.includes('No live game')) {
              setNoLiveGame(true)
            }
            disconnect(false)
            if (!noLiveGame && !isGameEndedOrNoGame(lastDataRef.current as SSEData)) {
              attemptReconnect()
            }
          }
        },
        onClose: () => {
          if (refs.isMounted.current) {
            console.log('SSE Connection closed')

            if (!isGameEndedOrNoGame(lastDataRef.current as SSEData)) {
              disconnect(false)
              attemptReconnect()
            } else {
              console.log('Connection closed due to game end or no game available')
              disconnect(false)
            }
          }
        },
      })
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        console.log('SSE Connection aborted')
        return
      }

      console.error('SSE Connection Error:', err)
      if (refs.isMounted.current) {
        setError(
          err instanceof Error
            ? err
            : new Error('Unknown error occurred while connecting to SSE')
        )
        setIsConnected(false)
        if (err instanceof Error && err.message.includes('No live game')) {
          setNoLiveGame(true)
        }
        disconnect(false)
        if (!noLiveGame && !isGameEndedOrNoGame(lastDataRef.current as SSEData)) {
          attemptReconnect()
        }
      }
    }
  }, [
    url,
    headers,
    isConnected,
    attemptReconnect,
    disconnect,
    isGameEndedOrNoGame,
    noLiveGame,
  ])

  useEffect(() => {
    refs.connect.current = connect
  }, [connect])

  const connectFn = useCallback(async () => {
    await connect()
  }, [connect])

  useEffect(() => {
    return () => {
      refs.isMounted.current = false
      disconnect(true)
    }
  }, [disconnect])

  return {
    data,
    error,
    isConnected,
    connect: connectFn,
    disconnect: () => disconnect(true),
  }
}

export default useServerSent
